import React, { useState, useEffect } from 'react';
import { Camera, MapPin, User, LogIn, Globe, Sparkles, Cloud, ShieldCheck } from 'lucide-react';
import { useLanguage } from './contexts/LanguageContext';
import { useAuth } from './contexts/AuthContext';
import FoodCameraModal from './components/FoodCameraModal';
import AuthModal from './components/AuthModal';
import UserProfileModal from './components/UserProfileModal';
import MapView from './components/MapView';

function App() {
  const { t, currentLanguage, changeLanguage, languages } = useLanguage();
  const { currentUser, userProfile, loading } = useAuth();

  // Tab navigation state - 'camera' is the default
  const [activeTab, setActiveTab] = useState('camera');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [appliedParams, setAppliedParams] = useState(null);

  // Track previous tab for animation direction
  const [prevTab, setPrevTab] = useState('camera');

  // Trigger for MapView to refresh data
  const [mapRefreshTrigger, setMapRefreshTrigger] = useState(0);

  // Callback when photo params are shared to a restaurant
  const handlePhotoShared = () => {
    console.log('📍 Photo shared, triggering map refresh');
    setMapRefreshTrigger(prev => prev + 1);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setPrevTab(activeTab);
      setActiveTab(tab);
    }
  };

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowLanguageMenu(false);
    if (showLanguageMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showLanguageMenu]);

  // Tab configuration
  const tabs = [
    { id: 'camera', icon: Camera, label: t('app.camera') || '相機' },
    { id: 'map', icon: MapPin, label: t('app.explore') || '探索' },
    { id: 'profile', icon: User, label: t('app.profile') || '個人' },
  ];

  return (
    <div className="app-shell">
      <div className="app-ambient" aria-hidden="true" />

      {/* App Content Area */}
      <div className="app-content" style={{ paddingBottom: 0 }}>
        {/* Camera Page */}
        {activeTab === 'camera' && (
          <div className="camera-page animate-fadeIn">
            <FoodCameraModal
              isOpen={true}
              onClose={() => { }} // Camera is always open as a page
              appliedParams={appliedParams}
              onParamsApplied={() => setAppliedParams(null)}
              onPhotoShared={handlePhotoShared} // Callback when photo is shared
              isEmbedded={true} // New prop to indicate embedded mode
            />
          </div>
        )}

        {/* Map/Explore Page */}
        {activeTab === 'map' && (
          <div className="map-page animate-fadeIn">
            <MapView
              isOpen={true}
              onClose={() => handleTabChange('camera')}
              onApplyParams={(params) => {
                setAppliedParams(params);
                handleTabChange('camera');
              }}
              refreshTrigger={mapRefreshTrigger} // Trigger refresh when photo is shared
              isEmbedded={true} // New prop to indicate embedded mode
            />
          </div>
        )}

        {/* Profile Page */}
        {activeTab === 'profile' && (
          <div className="profile-page animate-fadeIn">
            {currentUser ? (
              <UserProfileModal
                isOpen={true}
                onClose={() => handleTabChange('camera')}
                isEmbedded={true} // New prop to indicate embedded mode
              />
            ) : (
              // Login prompt for non-authenticated users
              <div className="profile-login-state animate-fadeIn">
                <div className="profile-login-panel">
                  <div className="mb-7">
                    <div className="profile-login-icon">
                      <User className="w-14 h-14 text-emerald-300" />
                    </div>
                  </div>

                  <h2 className="text-3xl font-extrabold text-white mb-3 text-center">
                    {t('profile.loginRequired') || '登入以查看個人資料'}
                  </h2>
                  <p className="text-gray-400 text-center mb-10 max-w-sm leading-relaxed font-medium">
                    {t('profile.loginDescription') || '登入後可以儲存照片、追蹤拍攝紀錄，並獲得個人化的 AI 建議'}
                  </p>

                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="btn-liquid px-10 py-4 text-base flex items-center gap-3"
                    aria-label={t('auth.login') || '登入'}
                  >
                    <LogIn className="w-6 h-6" />
                    {t('auth.login') || '登入'}
                  </button>

                  <div className="mt-14 grid grid-cols-3 gap-3 w-full max-w-md">
                    <div className="login-feature-tile">
                      <div className="login-feature-icon">
                        <Cloud className="w-5 h-5 text-sky-300" />
                      </div>
                      <span>{currentLanguage === 'zh-TW' ? '雲端備份' : 'Cloud Backup'}</span>
                    </div>
                    <div className="login-feature-tile">
                      <div className="login-feature-icon">
                        <Sparkles className="w-5 h-5 text-violet-300" />
                      </div>
                      <span>{currentLanguage === 'zh-TW' ? 'AI 建議' : 'AI Tips'}</span>
                    </div>
                    <div className="login-feature-tile">
                      <div className="login-feature-icon">
                        <ShieldCheck className="w-5 h-5 text-amber-300" />
                      </div>
                      <span>{currentLanguage === 'zh-TW' ? '成就系統' : 'Achievements'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Tab Bar - Redesigned Liquid Glass */}
      <div className="app-tabbar-wrap">
        <div className="app-tabbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`app-tabbar-item group ${activeTab === tab.id ? 'is-active' : ''
                }`}
              aria-label={tab.label}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              <tab.icon className="w-5 h-5 transition-transform duration-300 group-hover:scale-105" />
              <span>{tab.label}</span>
            </button>
          ))}

          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowLanguageMenu(!showLanguageMenu);
              }}
              className="app-tabbar-item"
              aria-label={currentLanguage === 'zh-TW' ? '切換語言' : 'Change language'}
              aria-expanded={showLanguageMenu}
              aria-haspopup="true"
            >
              <Globe className="w-5 h-5" />
              <span>{currentLanguage === 'zh-TW' ? '中文' : 'EN'}</span>
            </button>

            {showLanguageMenu && (
              <div className="absolute bottom-full right-0 mb-4 app-language-menu animate-slideUp">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setShowLanguageMenu(false);
                    }}
                    className={`w-full px-4 py-3 text-left text-sm font-medium rounded-xl transition-colors ${currentLanguage === lang.code
                      ? 'bg-green-500/20 text-green-400'
                      : 'text-white hover:bg-white/10'
                      }`}
                    aria-label={lang.nativeName}
                    aria-selected={currentLanguage === lang.code}
                  >
                    {lang.nativeName}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
}

export default App;
